// src/pages/SurveysPage.jsx
import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import "./SurveysPage.css";
import { useSurvey } from "../contexts/SurveyContext";
import { Helmet } from "react-helmet";


// Helper function to format date with an ordinal suffix
function formatDateWithSuffix(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = date.getDate();
  const year = date.getFullYear();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];

  let suffix = "th";
  if (day % 10 === 1 && day !== 11) suffix = "st";
  else if (day % 10 === 2 && day !== 12) suffix = "nd";
  else if (day % 10 === 3 && day !== 13) suffix = "rd";

  return `${day}${suffix} ${month} ${year}`;
}

function SurveysPage() {
  const navigate = useNavigate();
  const { surveys, loading, error } = useSurvey();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const [filterTags] = useState([]);

  // Filter & sort in useMemo
  const filteredSurveys = useMemo(() => {
    if (!surveys) return [];
    let result = [...surveys];

    // Filter by search query
    if (searchQuery.trim()) {
      const queryLower = searchQuery.toLowerCase();
      result = result.filter(
        (survey) =>
          (survey.title && survey.title.toLowerCase().includes(queryLower)) ||
          (survey.surveyType && survey.surveyType.toLowerCase().includes(queryLower))
      );
    }

    // Filter by tags
    if (filterTags.length > 0) {
      result = result.filter(
        (survey) =>
          survey.tags && filterTags.every((tag) => survey.tags.includes(tag))
      );
    }

    // Sort
    result.sort((a, b) => {
      return sortOrder === "latest"
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt);
    });

    return result;
  }, [surveys, searchQuery, sortOrder, filterTags]);

  const handleSortChange = () => {
    setSortOrder((prev) => (prev === "latest" ? "chronological" : "latest"));
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Navigate to /surveys/:id/details
  const handleGoToSurvey = (surveyId) => {
    navigate(`/surveys/${surveyId}/details`);
  };

  if (loading) {
    return <div className="loading-container">Loading surveys...</div>;
  }

  if (error) {
    return <div className="loading-container">Error: {error}</div>;
  }

<Helmet>
  <title>All your surveys | survai</title>
  <meta name="description" content="View and manage all your surveys on survai." />
</Helmet>

  return (
    <div className="app-container">
      <Sidebar />

      <div className="content">
        <h1 className="page-title">My Surveys</h1>
        <div className="survey-info">
          <span>Total Surveys: {filteredSurveys.length}</span>
        </div>

        <div className="surveys-controls">
          <button onClick={handleSortChange} className="sort-button">
            Sort by: {sortOrder === "latest" ? "Latest" : "Chronological"}
          </button>

          <div className="search-container">
            <i className="fa-light fa-magnifying-glass search-icon" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Search surveys..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        </div>

        <table className="surveys-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Created On</th>
              <th>Tags</th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {filteredSurveys.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-surveys">
                  No surveys found.
                </td>
              </tr>
            ) : (
              filteredSurveys.map((survey) => (
                <tr key={survey._id}>
                  <td>
                    <div className="survey-item-title">
                      {survey.title || "Untitled Survey"}
                    </div>
                    <div className="survey-item-type">
                      {survey.surveyType || "N/A"}
                    </div>
                  </td>
                  <td>{formatDateWithSuffix(survey.createdAt)}</td>
                  <td>{survey.tags?.join(", ") || "—"}</td>
                  <td className="icon-cell">
                    <Link
                      to={`/surveys/${survey._id}/details`}
                      className="view-link"
                    >
                      <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                    {/* Or:
                      <i
                        className="fa-solid fa-arrow-right"
                        onClick={() => handleGoToSurvey(survey._id)}
                      />
                    */}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SurveysPage;
